import { useState, useMemo, useEffect, useRef } from "react";
import { useAuth } from "./contexts/AuthContext";
import useSupabaseTable from "./hooks/useSupabaseTable";
import useToday from "./hooks/useToday";
import { CLIENT_STAGES, PARTNER_STAGES, PARTNER_TYPES, ROLE_ACCESS } from "./constants";
import Login from "./pages/Login";
import ForceChangePassword from "./pages/ForceChangePassword";

// Components
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import AvisoSyncFalhou from "./components/AvisoSyncFalhou";

// Pages
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import ClientPipeline from "./pages/ClientPipeline";
import Partners from "./pages/Partners";
import RedeParceiros from "./pages/RedeParceiros";
import PartnerPipeline from "./pages/PartnerPipeline";
import Tasks from "./pages/Tasks";
import Reports from "./pages/Reports";
import Team from "./pages/Team";
import Contracts from "./pages/Contracts";
import Satisfaction from "./pages/Satisfaction";
import Management from "./pages/Management";
import Credenciamentos from "./pages/Credenciamentos";
import ContratosParceiros from "./pages/ContratosParceiros";

// Modals
import ClientDetail from "./modals/ClientDetail";
import PartnerDetail from "./modals/PartnerDetail";
import BannerPesquisa from "./components/BannerPesquisa";
import NewClient from "./modals/NewClient";
import QuickClient from "./modals/QuickClient";
import AdesaoCliente from "./modals/AdesaoCliente";
import NewPartner from "./modals/NewPartner";
import NewInteraction, { RASCUNHO_INTERACAO } from "./modals/NewInteraction";
import NewContract from "./modals/NewContract";
import ContractDetail from "./modals/ContractDetail";
import NewSatisfaction from "./modals/NewSatisfaction";
import NewCredenciamento from "./modals/NewCredenciamento";
import CobrancaAbordagem from "./modals/CobrancaAbordagem";
import CobrancaMeta from "./modals/CobrancaMeta";
import ConferenciaGerente from "./modals/ConferenciaGerente";
import FilaConferencia from "./modals/FilaConferencia";
import FichaDetalhe from "./modals/FichaDetalhe";
import QuickFicha, { RASCUNHO_QUICKFICHA } from "./modals/QuickFicha";
import { lerRascunho } from "./lib/rascunho";
import { aCobrar } from "./lib/abordagem";
import { unificarRede } from "./lib/rede";
import { avancoAutomatico, aplicarAvanco } from "./lib/funil";
import SendFichaLink from "./modals/SendFichaLink";
import SendPesquisaLink from "./modals/SendPesquisaLink";

export default function App() {
  const { user, profile, loading: authLoading, signOut, reloadProfile } = useAuth();
  const today = useToday();

  // ── State: Navigation ── (persistido: sobrevive a recarga/descarte de aba no tablet)
  const [mod, setMod] = useState(() => {
    try { return localStorage.getItem("crm_mod") || "dashboard"; } catch { return "dashboard"; }
  });
  useEffect(() => {
    try { localStorage.setItem("crm_mod", mod); } catch { /* ignora */ }
  }, [mod]);
  const [collapsed, setCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [notifOpen, setNotifOpen] = useState(false);

  // ── State: Data (persisted no Supabase) ──
  const [clients, setClients, clientsLoading] = useSupabaseTable("clients", user?.id);
  const [partners, setPartners, partnersLoading] = useSupabaseTable("partners", user?.id);
  const [tasks, setTasks, tasksLoading] = useSupabaseTable("tasks", user?.id);
  const [contracts, setContracts, contractsLoading] = useSupabaseTable("contracts", user?.id);
  const [satisfaction, setSatisfaction, satisfactionLoading] = useSupabaseTable("satisfaction", user?.id);
  const [mgmtReports, setMgmtReports, mgmtLoading] = useSupabaseTable("management_reports", user?.id);
  const [credenciamentos, setCredenciamentos, credLoading] = useSupabaseTable("credenciamentos", user?.id);
  const [pesquisaConfig, setPesquisaConfig] = useSupabaseTable("pesquisa_config", user?.id);
  const [metas, setMetas] = useSupabaseTable("metas", user?.id);
  const [roteiro, setRoteiro] = useSupabaseTable("roteiro", user?.id);
  const dataLoading = clientsLoading || partnersLoading || tasksLoading || contractsLoading || satisfactionLoading || mgmtLoading || credLoading;

  // ── State: Modals ──
  const [selClient, setSelClient] = useState(null);
  const [selPartner, setSelPartner] = useState(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [showQuickClient, setShowQuickClient] = useState(false);
  const [showNewPartner, setShowNewPartner] = useState(false);
  const [showNewInteraction, setShowNewInteraction] = useState(null);
  const [showNewContract, setShowNewContract] = useState(false);
  const [selContract, setSelContract] = useState(null);
  const [showNewSatisfaction, setShowNewSatisfaction] = useState(false);
  const [showNewCredenciamento, setShowNewCredenciamento] = useState(false);
  const [credEdit, setCredEdit] = useState(null);
  const [showSendLink, setShowSendLink] = useState(false);
  const [sendLinkPartner, setSendLinkPartner] = useState(null);
  const [showSendPesquisa, setShowSendPesquisa] = useState(false);
  const [showQuickFicha, setShowQuickFicha] = useState(false);
  const [adesaoDe, setAdesaoDe] = useState(undefined);  // undefined = fechado; null = cliente novo
  const openSendLink = (p = null) => { setSendLinkPartner(p); setShowSendLink(true); };

  // ── Cobrança de abordagem parada ──
  // "Agora não" vale só até recarregar: o alerta não pode ser desligado de vez,
  // senão a empresa some do radar exatamente como sumia antes.
  const [cobrancaAdiada, setCobrancaAdiada] = useState(false);
  const [cobrancaFoco, setCobrancaFoco] = useState(null);
  const [conferindo, setConferindo] = useState(null);
  const [fichaDetalhe, setFichaDetalhe] = useState(null);
  const [filaAberta, setFilaAberta] = useState(false);
  // A cobrança de meta aparece uma vez por sessão: ela cobra o que já fechou,
  // e repetir a mesma conversa a cada troca de aba vira ruído.
  const [metaVista, setMetaVista] = useState(false);
  const [grupoRede, setGrupoRede] = useState(null);

  // ── Role ──
  const role = profile?.role || "comercial";
  const allowed = ROLE_ACCESS[role] || ROLE_ACCESS.comercial;

  // ── Data filtered by role (comercial sees only own data) ──
  const visibleClients = useMemo(
    () => role === "comercial" ? clients.filter((c) => c.userId === user?.id) : clients,
    [clients, role, user?.id]
  );
  const visiblePartners = useMemo(
    () => role === "comercial" ? partners.filter((p) => p.userId === user?.id) : partners,
    [partners, role, user?.id]
  );
  const visibleTasks = useMemo(
    () => role === "comercial" ? tasks.filter((t) => t.userId === user?.id) : tasks,
    [tasks, role, user?.id]
  );
  const visibleContracts = useMemo(
    () => role === "comercial" ? contracts.filter((c) => c.userId === user?.id) : contracts,
    [contracts, role, user?.id]
  );
  const visibleSatisfaction = useMemo(
    () => role === "comercial" ? satisfaction.filter((s) => s.userId === user?.id) : satisfaction,
    [satisfaction, role, user?.id]
  );
  // Fichas/Credenciamento são compartilhadas: TODOS os usuários veem todo o conteúdo
  const visibleCredenciamentos = credenciamentos;

  // ── A Rede é UMA rede ───────────────────────────────────────────
  //
  // As fichas já eram de todos, mas os parceiros não: o comercial via só os
  // dele. O resultado era um sistema que discordava de si mesmo — a mesma
  // empresa aparecia em "Abordagem" para o administrador e em "A contatar" para
  // quem ia trabalhá-la, porque, sem enxergar o parceiro do outro, a tela caía
  // na ficha e derivava o estágio de novo (foi o caso do Gallotti Trucks). E as
  // 300+ empresas ainda a contatar, que não são de ninguém, ficavam escondidas
  // de quem existe para contatá-las.
  //
  // Aqui a Rede recebe tudo e o filtro "Responsável" decide o que olhar. Quem
  // responde pelo quê continua gravado em `userId` — o que mudou é que a lista
  // deixou de ser o critério de visibilidade. As outras abas (Dashboard,
  // Parceiros, relatórios) seguem com o recorte de cada um.
  const [usuarios, setUsuarios] = useState([]);
  useEffect(() => {
    if (!user?.id) return;
    let vivo = true;
    import("./lib/supabase").then(({ supabase }) =>
      supabase.from("profiles").select("id, full_name, role").order("full_name")
    ).then(({ data }) => { if (vivo && data) setUsuarios(data); })
      .catch((e) => console.error("Não consegui carregar a equipe:", e));
    return () => { vivo = false; };
  }, [user?.id]);

  // ── Computed ──
  const overdueTasks = useMemo(() => visibleTasks.filter((t) => !t.done && t.dueDate < today), [visibleTasks, today]);
  const todayTasks = useMemo(() => visibleTasks.filter((t) => !t.done && t.dueDate === today), [visibleTasks, today]);
  const upcomingTasks = useMemo(
    () => visibleTasks.filter((t) => !t.done && t.dueDate > today).sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [visibleTasks, today]
  );

  const clientKpis = useMemo(() => {
    const active = visibleClients.filter((c) => !["ganho", "perdido", "descartado"].includes(c.stage));
    const pipeline = active.reduce((s, c) => s + (parseInt(c.estimatedValue) || 0), 0);
    const byStage = {};
    CLIENT_STAGES.forEach((s) => { byStage[s.id] = visibleClients.filter((c) => c.stage === s.id).length; });
    return { total: visibleClients.length, active: active.length, pipeline, byStage, won: visibleClients.filter((c) => c.stage === "ganho").length };
  }, [visibleClients]);

  const partnerKpis = useMemo(() => {
    const byType = {};
    PARTNER_TYPES.forEach((t) => { byType[t.id] = visiblePartners.filter((p) => p.type === t.id).length; });
    const byStage = {};
    PARTNER_STAGES.forEach((s) => { byStage[s.id] = visiblePartners.filter((p) => p.stage === s.id).length; });
    return {
      total: visiblePartners.length,
      active: visiblePartners.filter((p) => p.stage === "ativo").length,
      byType, byStage,
      inProgress: visiblePartners.filter((p) => !["ativo", "recusado", "sem_interesse"].includes(p.stage)).length,
    };
  }, [visiblePartners]);

  // Cada um responde pelo que é seu: a cobrança olha os parceiros do próprio
  // usuário, e não a rede inteira — inclusive para gerente e administrador.
  const meusParceiros = useMemo(
    () => partners.filter((p) => p.userId === user?.id),
    [partners, user?.id]
  );
  const pendentesCobranca = useMemo(() => aCobrar(meusParceiros, today), [meusParceiros, today]);
  const listaCobranca = useMemo(
    () => (cobrancaFoco ? partners.filter((p) => p.id === cobrancaFoco) : pendentesCobranca),
    [cobrancaFoco, partners, pendentesCobranca]
  );
  const cobrancaAberta = listaCobranca.length > 0 && (
    !!cobrancaFoco || (!cobrancaAdiada && ["dashboard", "rede"].includes(mod))
  );

  // ── Rascunho pendente: reabre o formulário de onde parou ──
  // No tablet o Android pode fechar o navegador no meio do registro (câmera,
  // troca de app). Quando a aba volta recarregada, o formulário reabre com o
  // que já estava preenchido — uma vez só, na abertura.
  const rascunhoVisto = useRef(false);
  useEffect(() => {
    if (dataLoading || rascunhoVisto.current) return;
    rascunhoVisto.current = true;
    if (lerRascunho(RASCUNHO_QUICKFICHA)) { setShowQuickFicha(true); return; }
    // Ficha de visita (nova) com algo preenchido: o próprio modal recupera o
    // rascunho ao abrir — aqui só reabre.
    try {
      const f = JSON.parse(localStorage.getItem("cred_draft_v1") || "null");
      const docs = JSON.parse(localStorage.getItem("cred_draft_v1_docs") || "[]");
      if ((f && (f.nomeFantasia || f.razaoSocial || f.cnpj || f.responsavelNome)) || (Array.isArray(docs) && docs.length)) {
        setCredEdit(null); setShowNewCredenciamento(true); return;
      }
    } catch { /* rascunho ilegível: ignora */ }
    const ri = lerRascunho(RASCUNHO_INTERACAO);
    if (ri?.target?.id) setShowNewInteraction(ri.target);
  }, [dataLoading]);

  // ── O fim do funil que anda sozinho ──
  //
  // Ficha assinada e sem documento pendente move para Validação; fornecedor
  // criado no sistema move para Ativo. Ninguém clica: o fato é que decide, e a
  // trilha guarda qual fato foi. Só para frente — documento removido depois
  // não devolve a empresa para o estágio anterior.
  useEffect(() => {
    if (dataLoading) return;
    const movs = [];
    for (const item of unificarRede(partners, credenciamentos)) {
      if (!item.parceiro) continue;
      const mov = avancoAutomatico(item.parceiro, item.ficha);
      if (mov) movs.push({ id: item.parceiro.id, mov });
    }
    if (!movs.length) return;
    setPartners((prev) => prev.map((p) => {
      const achado = movs.find((m) => m.id === p.id);
      return achado ? aplicarAvanco(p, achado.mov) : p;
    }));
  }, [partners, credenciamentos, dataLoading, setPartners]);

  const notifs = useMemo(() => {
    const n = [];
    overdueTasks.forEach((t) => n.push({ text: `Atrasada: ${t.title}`, type: "warn" }));
    todayTasks.forEach((t) => n.push({ text: `Hoje: ${t.title}`, type: "info" }));
    visibleClients
      .filter((c) => c.nextActionDate && c.nextActionDate <= today && !["ganho", "perdido", "descartado"].includes(c.stage))
      .forEach((c) => n.push({ text: `Follow-up: ${c.company}`, type: "info" }));
    // Parceiro com compromisso vencido nunca aparecia aqui: as notificações só
    // olhavam clientes e contratos, e a retomada agendada morria em silêncio.
    visiblePartners
      .filter((p) => p.nextActionDate && p.nextActionDate <= today && !["ativo", "recusado"].includes(p.stage))
      .forEach((p) => n.push({ text: `${p.stage === "sem_interesse" ? "Retomar" : "Follow-up"}: ${p.name}`, type: "warn" }));
    visibleContracts
      .filter((c) => c.status === "ativo" && c.endDate && Math.ceil((new Date(c.endDate) - new Date(today)) / 86400000) <= 30 && Math.ceil((new Date(c.endDate) - new Date(today)) / 86400000) >= 0)
      .forEach((c) => n.push({ text: `Contrato vencendo: ${c.clientName}`, type: "warn" }));
    return n;
  }, [overdueTasks, todayTasks, visibleClients, visiblePartners, visibleContracts, today]);

  // ── Actions ──
  const toggleTask = (id) => setTasks((p) => p.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));
  const moveClient = (id, stage) => setClients((p) => p.map((c) => (c.id === id ? { ...c, stage } : c)));
  const movePartner = (id, stage) => setPartners((p) => p.map((p2) => (p2.id === id ? { ...p2, stage } : p2)));
  const deleteClient = (id) => setClients((p) => p.filter((c) => c.id !== id));
  const deletePartner = (id) => setPartners((p) => p.filter((p2) => p2.id !== id));

  // ── Module router ──
  const renderMod = () => {
    // Bloqueia acesso a módulos não permitidos
    if (!allowed.includes(mod)) {
      return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Você não tem acesso a este módulo.</div>;
    }
    const shared = { today, search };
    switch (mod) {
      case "dashboard":
        return <Dashboard {...shared} clients={visibleClients} partners={visiblePartners} tasks={visibleTasks} toggleTask={toggleTask} overdueTasks={overdueTasks} todayTasks={todayTasks} upcomingTasks={upcomingTasks} clientKpis={clientKpis} partnerKpis={partnerKpis} contracts={visibleContracts} satisfaction={visibleSatisfaction} setSelClient={setSelClient} setSelPartner={setSelPartner} setShowNewInteraction={setShowNewInteraction} setMod={setMod} />;
      case "clients":
        return <Clients {...shared} clients={visibleClients} setClients={setClients} stageFilter={stageFilter} setStageFilter={setStageFilter} clientKpis={clientKpis} setShowNewClient={setShowNewClient} setSelClient={setSelClient} onAdesao={(c) => setAdesaoDe(c)} role={role} userId={user?.id} metas={metas} onRegistroRapido={() => setShowQuickClient(true)} setShowNewInteraction={setShowNewInteraction} />;
      case "client_pipeline":
        return <ClientPipeline {...shared} clients={visibleClients} setSelClient={setSelClient} />;
      case "rede":
        // A aba única: parceiro e ficha da mesma empresa no mesmo card.
        return (
          <RedeParceiros
            {...shared}
            partners={partners} setPartners={setPartners}
            credenciamentos={visibleCredenciamentos} setCredenciamentos={setCredenciamentos}
            userName={profile?.full_name}
            usuarios={usuarios}
            role={role}
            metas={metas}
            userId={user?.id}
            setShowNewPartner={setShowNewPartner}
            setSelPartner={setSelPartner}
            setShowNewInteraction={setShowNewInteraction}
            onSendLink={openSendLink}
            onEditFicha={(rec) => setFichaDetalhe(rec.id)}
            onPedirDocumentos={(rec) => setFichaDetalhe(rec.id)}
            onJustificar={(id) => setCobrancaFoco(id)}
            onConferir={(id) => setConferindo(id)}
            onAbrirFila={() => setFilaAberta(true)}
            onNovaFicha={() => { setCredEdit(null); setShowNewCredenciamento(true); }}
            onRegistroRapido={() => setShowQuickFicha(true)}
            setTasks={setTasks}
            grupoInicial={grupoRede}
            onGrupoAplicado={() => setGrupoRede(null)}
          />
        );
      case "partners":
        return <Partners {...shared} partners={visiblePartners} credenciamentos={visibleCredenciamentos} setShowNewPartner={setShowNewPartner} setSelPartner={setSelPartner} onSendLink={openSendLink} />;
      case "partner_pipeline":
        return <PartnerPipeline partners={visiblePartners} setSelPartner={setSelPartner} />;
      case "credenciamentos":
        // Fichas é a tela de DOCUMENTAÇÃO: acompanhar, completar e cobrar o que
        // falta. Nascer, não — toda empresa entra pela Rede de Parceiros, que é
        // onde o contato acontece. Por isso "Registro rápido", "Enviar
        // formulário" e "Nova ficha" saíram daqui (`onContinuarRascunho`
        // permanece: ele só recupera um preenchimento interrompido).
        return <Credenciamentos {...shared} credenciamentos={visibleCredenciamentos} partners={partners} onVerNaRede={(p) => { setMod("rede"); setSelPartner(p); }} setCredenciamentos={setCredenciamentos} onContinuarRascunho={() => { setCredEdit(null); setShowNewCredenciamento(true); }} onEdit={(rec) => { setCredEdit(rec); setShowNewCredenciamento(true); }} onIrParaRede={() => setMod("rede")} userName={profile?.full_name} />;
      case "contratos_parceiros":
        return <ContratosParceiros credenciamentos={visibleCredenciamentos} search={search} />;
      case "tasks":
        return <Tasks
          tasks={visibleTasks} today={today} toggleTask={toggleTask}
          overdueTasks={overdueTasks} todayTasks={todayTasks} upcomingTasks={upcomingTasks}
          partners={visiblePartners} clients={visibleClients} setTasks={setTasks} userId={user?.id}
          onAbrirParceiro={(p) => setSelPartner(p)}
          onAbrirCliente={(c) => setSelClient(c)}
          onRegistrarContato={(p, taskId) => setShowNewInteraction({ type: "partner", id: p.id, taskId })}
        />;
      case "contracts":
        return <Contracts contracts={visibleContracts} clients={visibleClients} today={today} search={search} setShowNewContract={setShowNewContract} setSelContract={setSelContract} />;
      case "satisfaction":
        return <Satisfaction satisfaction={visibleSatisfaction} today={today} search={search} onSendPesquisa={() => setShowSendPesquisa(true)} pesquisaConfig={pesquisaConfig} setPesquisaConfig={setPesquisaConfig} />;
      case "reports":
        return <Reports today={today} tasks={visibleTasks} clientKpis={clientKpis} partnerKpis={partnerKpis} overdueTasks={overdueTasks} partners={visiblePartners} credenciamentos={visibleCredenciamentos} satisfaction={visibleSatisfaction} metas={metas} role={role} />;
      case "management":
        return <Management reports={mgmtReports} setReports={setMgmtReports} userId={user?.id} today={today} />;
      case "team":
        return <Team currentRole={role} metas={metas} setMetas={setMetas} roteiro={roteiro} setRoteiro={setRoteiro} userName={profile?.full_name} />;
      default:
        return <Dashboard {...shared} clients={visibleClients} partners={visiblePartners} tasks={visibleTasks} toggleTask={toggleTask} overdueTasks={overdueTasks} todayTasks={todayTasks} upcomingTasks={upcomingTasks} clientKpis={clientKpis} partnerKpis={partnerKpis} contracts={visibleContracts} satisfaction={visibleSatisfaction} setSelClient={setSelClient} setSelPartner={setSelPartner} setShowNewInteraction={setShowNewInteraction} setMod={setMod} />;
    }
  };

  // ── Auth gate ──
  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", background: "#FAFAFA" }}>
        <div style={{ textAlign: "center", color: "#555" }}>
          <div style={{ width: 36, height: 36, border: "3px solid #e2e8f0", borderTop: "3px solid #251C59", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 12px" }} />
          <p style={{ fontSize: 14 }}>Carregando...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      </div>
    );
  }

  if (!user) return <Login />;

  // Conta sem papel da equipe (criada fora do painel Equipe, ou ainda não
  // liberada): não vê dado nenhum, o banco já recusa — aqui só se explica.
  if (profile && !ROLE_ACCESS[profile.role]) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", background: "#FAFAFA", padding: 16 }}>
        <div style={{ maxWidth: 380, textAlign: "center", color: "#555" }}>
          <h2 style={{ fontSize: 18, color: "#251C59", marginBottom: 8 }}>Acesso ainda não liberado</h2>
          <p style={{ fontSize: 14, marginBottom: 16 }}>Peça ao administrador da equipe comercial para liberar o seu acesso.</p>
          <button onClick={signOut} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 14 }}>Sair</button>
        </div>
      </div>
    );
  }

  if (profile?.must_change_password) {
    return <ForceChangePassword profile={profile} onDone={reloadProfile} />;
  }

  if (dataLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", background: "#FAFAFA" }}>
        <div style={{ textAlign: "center", color: "#555" }}>
          <div style={{ width: 36, height: 36, border: "3px solid #e2e8f0", borderTop: "3px solid #251C59", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 12px" }} />
          <p style={{ fontSize: 14 }}>Carregando dados...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", background: "#FAFAFA", color: "#333333", fontSize: 14 }}>
      <Sidebar mod={mod} setMod={setMod} collapsed={collapsed} setCollapsed={setCollapsed} setStageFilter={setStageFilter} signOut={signOut} role={role} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        <TopBar mod={mod} search={search} setSearch={setSearch} notifs={notifs} notifOpen={notifOpen} setNotifOpen={setNotifOpen} userName={profile?.full_name} role={role} />
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {/* Régua de relacionamento: parceiro ativo não reclama até ir embora.
              Só a gerência vê, e some quando ninguém está vencido. */}
          {["dashboard", "rede", "satisfaction"].includes(mod) && (
            <div style={{ marginBottom: 14 }}>
              <BannerPesquisa
                partners={visiblePartners}
                satisfaction={visibleSatisfaction}
                hoje={today}
                role={role}
                onEnviar={() => setShowSendPesquisa(true)}
              />
            </div>
          )}
          {renderMod()}
        </div>
      </div>

      {/* Gravação que falhou em segundo plano — o único jeito de quem está
          usando o sistema descobrir que o que está na tela não foi salvo. */}
      <AvisoSyncFalhou />

      {/* ══════════ MODALS ══════════ */}
      <ClientDetail
        client={selClient}
        onClose={() => setSelClient(null)}
        clients={clients}
        setClients={setClients}
        moveClient={moveClient}
        setShowNewInteraction={setShowNewInteraction}
        today={today}
        onDelete={deleteClient}
        contracts={visibleContracts}
        satisfaction={visibleSatisfaction}
        onAdesao={(c) => setAdesaoDe(c)}
        userName={profile?.full_name}
      />

      {/* A porta: cliente de prospecção, licitação, indicação ou trazido por
          parceiro nasce aqui e sai com as ativações agendadas. */}
      <AdesaoCliente
        open={adesaoDe !== undefined}
        cliente={adesaoDe || null}
        onClose={() => setAdesaoDe(undefined)}
        setClients={setClients}
        setTasks={setTasks}
        userId={user?.id}
        userName={profile?.full_name}
      />

      <PartnerDetail
        partner={selPartner}
        onClose={() => setSelPartner(null)}
        setPartners={setPartners}
        movePartner={movePartner}
        setShowNewInteraction={setShowNewInteraction}
        onDelete={deletePartner}
        credenciamentos={visibleCredenciamentos}
        onSendLink={openSendLink}
        onEditFicha={(c) => { setSelPartner(null); setCredEdit(c); setShowNewCredenciamento(true); }}
        userName={profile?.full_name}
      />

      <NewClient open={showNewClient} onClose={() => setShowNewClient(false)} setClients={setClients} setTasks={setTasks} userId={user?.id} />
      <QuickClient open={showQuickClient} onClose={() => setShowQuickClient(false)} setClients={setClients} setTasks={setTasks} userId={user?.id} />
      <NewPartner open={showNewPartner} onClose={() => setShowNewPartner(false)} setPartners={setPartners} setTasks={setTasks} userId={user?.id} role={role} />
      <NewInteraction
        target={showNewInteraction}
        onClose={() => setShowNewInteraction(null)}
        clients={clients} setClients={setClients}
        partners={partners} setPartners={setPartners}
        setTasks={setTasks}
        userId={user?.id}
        userName={profile?.full_name}
        onAceitouCredenciar={openSendLink}
      />

      {/* Abordagem parada há mais de 2 dias: quem sumiu precisa de explicação,
          e a explicação fica registrada com autor, motivo e nova tentativa. */}
      <CobrancaAbordagem
        open={cobrancaAberta}
        pendentes={listaCobranca}
        setPartners={setPartners}
        setTasks={setTasks}
        userId={user?.id}
        userName={profile?.full_name}
        onRegistrarContato={(id) => { setCobrancaFoco(null); setCobrancaAdiada(true); setShowNewInteraction({ type: "partner", id }); }}
        onAdiar={() => { setCobrancaFoco(null); setCobrancaAdiada(true); }}
      />

      {/* O comercial executa, a gerência confere e avalia. A avaliação fica na
          trilha, com autor — é o que fecha o ciclo e melhora o próximo contato. */}
      {/* A ficha inteira aberta de dentro da Rede: pedir documento, emitir o
          Cartão CNPJ e ver o vínculo com os três sistemas sem trocar de aba. */}
      <FichaDetalhe
        ficha={fichaDetalhe ? credenciamentos.find((f) => f.id === fichaDetalhe) || null : null}
        onClose={() => setFichaDetalhe(null)}
        setCredenciamentos={setCredenciamentos}
        onEdit={(rec) => { setFichaDetalhe(null); setCredEdit(rec); setShowNewCredenciamento(true); }}
        userName={profile?.full_name}
      />

      {/* Meta do dia: cobra o dia útil que já fechou abaixo do combinado. */}
      <CobrancaMeta
        open={!metaVista && !cobrancaAberta && ["dashboard", "rede"].includes(mod)}
        partners={visiblePartners}
        metas={metas}
        userId={user?.id}
        hoje={today}
        onClose={() => setMetaVista(true)}
        onRegistrar={() => { setMetaVista(true); setGrupoRede("novos"); setMod("rede"); }}
      />

      {/* A mesma conferência, em fila corrida: para 350 parceiros, caçar card
          a card não é trabalho de gerente. */}
      <FilaConferencia
        open={filaAberta}
        partners={visiblePartners}
        setPartners={setPartners}
        userName={profile?.full_name}
        onClose={() => setFilaAberta(false)}
        onAbrirEmpresa={(p) => { setFilaAberta(false); setSelPartner(p); }}
      />

      <ConferenciaGerente
        parceiro={conferindo ? partners.find((p) => p.id === conferindo) || null : null}
        onClose={() => setConferindo(null)}
        setPartners={setPartners}
        userName={profile?.full_name}
      />
      <NewContract open={showNewContract} onClose={() => setShowNewContract(false)} setContracts={setContracts} clients={visibleClients} userId={user?.id} />
      <ContractDetail contract={selContract} onClose={() => setSelContract(null)} setContracts={setContracts} today={today} />
      <NewSatisfaction open={showNewSatisfaction} onClose={() => setShowNewSatisfaction(false)} setSatisfaction={setSatisfaction} clients={visibleClients} userId={user?.id} />
      <NewCredenciamento open={showNewCredenciamento} editing={credEdit} onClose={() => { setShowNewCredenciamento(false); setCredEdit(null); }} setCredenciamentos={setCredenciamentos} partners={visiblePartners} userId={user?.id} userName={profile?.full_name} />
      <SendFichaLink open={showSendLink} onClose={() => { setShowSendLink(false); setSendLinkPartner(null); }} partners={partners} clients={visibleClients} credenciamentos={credenciamentos} userId={user?.id} presetPartner={sendLinkPartner} />
      <SendPesquisaLink open={showSendPesquisa} onClose={() => setShowSendPesquisa(false)} clients={visibleClients} partners={visiblePartners} userId={user?.id} />
      <QuickFicha open={showQuickFicha} onClose={() => setShowQuickFicha(false)} setCredenciamentos={setCredenciamentos} userId={user?.id} userName={profile?.full_name} role={role} />

      <style>{`
        * { scrollbar-width: thin; scrollbar-color: #d1d5db transparent; box-sizing: border-box; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
        tr:hover { background: #FAFAFA; }
        @media (max-width: 768px) {
          .crm-sidebar { width: 52px !important; }
        }
      `}</style>
    </div>
  );
}
